// Typed client for per-user broker routing:
//   GET /api/BrokerSelection        — options per asset class + current selection
//   PUT /api/BrokerSelection        — set (or clear) the broker for an asset class
// A null `selected` means "use the registry default".

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export interface BrokerClassSelection {
  assetClass: string;        // "Equity" | "Forex" | "Crypto"
  selected: string | null;   // chosen provider, or null = default
  default: string | null;    // the registry default provider
  options: string[];         // all providers serving this class
}

export interface BrokerSelection {
  equity: BrokerClassSelection;
  forex: BrokerClassSelection;
  // The API has always returned this; the type and the settings page simply did not
  // declare it, so Kraken and Binance were unreachable from the UI and a null crypto
  // preference silently resolved to whichever factory registered first (Coinbase).
  crypto: BrokerClassSelection;
}

export interface BrokerIntegrationCatalogEntry {
  rank: number;
  code: string;
  displayName: string;
  connector: string;
  /** 'preview' = an adapter exists but has not been certified against the broker's own environment. */
  implementationStatus: 'available' | 'preview' | 'planned' | 'blocked';
  accessStatus: 'public-direct' | 'approval-required' | 'commercial-required' | 'commercial-discovery' | 'terminal-agent';
  paperTrading: string;
  credentialFlow: string;
  /** BrokerApi | BrokerNativePlatform | TerminalAgent | BrokerDaemon. A platform
   *  connector is not the broker: the account still executes at a real brokerage. */
  connectorKind: string;
  provider: string | null;
  assetClasses: string[];
  caveats: string[];
}

async function getJson<T>(path: string): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error((await res.text()) || `Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

export const brokerSelectionService = {
  get: () => getJson<BrokerSelection>('/api/BrokerSelection'),

  getCatalog: () => getJson<BrokerIntegrationCatalogEntry[]>('/api/BrokerSelection/catalog'),

  // provider === null clears the selection (falls back to the default).
  set: async (assetClass: string, provider: string | null): Promise<BrokerSelection> => {
    const res = await apiFetch(`${API_BASE}/api/BrokerSelection`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assetClass, provider }),
    });
    if (!res.ok) throw new Error((await res.text()) || `Request failed (${res.status})`);
    return res.json() as Promise<BrokerSelection>;
  },
};
